import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Cookie, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { loadGTM } from '@/utils/gtmLoader';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const { currentLanguage } = useLanguage();
  const { showSettings, closeCookieSettings } = useCookieConsent();
  const { isMaintenanceMode } = useMaintenanceMode();
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const saved = JSON.parse(consent);
      setPreferences(saved);
      applyCookieSettings(saved);
    }
  }, []);

  // Listen for changes from context to open settings
  useEffect(() => {
    if (showSettings) {
      // Load existing preferences when opening settings
      const consent = localStorage.getItem('cookie-consent');
      if (consent) {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      }
      setShowBanner(true);
      setShowSettingsPanel(true);
    }
  }, [showSettings]);

  const applyCookieSettings = (prefs: CookiePreferences) => {
    if (!prefs.analytics && typeof window !== 'undefined') {
      (window as any)['ga-disable-GA_MEASUREMENT_ID'] = true;
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    saveConsent(allAccepted);
  };

  const acceptSelected = () => {
    saveConsent(preferences);
  };

  const rejectNonEssential = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    saveConsent(onlyNecessary);
  };

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs));
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    applyCookieSettings(prefs);
    setShowBanner(false);
    setShowSettingsPanel(false);
    closeCookieSettings();
    
    // Load GTM if analytics or marketing cookies are accepted
    if (prefs.analytics || prefs.marketing) {
      loadGTM();
    }
  };

  const handleClose = () => {
    setShowBanner(false);
    setShowSettingsPanel(false);
    closeCookieSettings();
  };

  // Don't show cookie banner during maintenance mode
  if (!showBanner || isMaintenanceMode) return null;

  return (
    <div className="fixed inset-0 md:inset-auto md:bottom-0 md:left-0 md:right-0 z-[60] bg-black/40 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none animate-fade-in flex items-end justify-center p-2 md:p-4">
      <Card className="w-full max-w-[calc(100vw-1rem)] md:max-w-4xl mx-auto rounded-t-2xl md:rounded-2xl border-0 md:border shadow-2xl bg-background overflow-hidden">
        {/* Close Button - Top Right */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={handleClose}
          className="absolute top-2 right-2 h-8 w-8 rounded-full hover:bg-background/50 z-10"
          aria-label="Stäng cookie-meddelande"
        >
          <X className="w-4 h-4" />
        </Button>

        {/* Main Content - Horizontal on Desktop */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 md:p-6">
          {/* Left: Icon + Text */}
          <div className="flex items-center gap-3 lg:flex-1 pr-8 lg:pr-0">
            <div className="p-2 rounded-full bg-primary/10 flex-shrink-0">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-base md:text-lg mb-1">Cookies</h3>
              {!showSettingsPanel ? (
                <div className="space-y-1.5">
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                    Vi använder cookies för att förbättra din upplevelse och analysera hur sajten används.
                  </p>
                  <Link 
                    to="/cookies" 
                    className="text-xs md:text-sm text-primary hover:underline font-medium inline-flex items-center gap-1"
                  >
                    Läs mer om cookies
                    <ChevronDown className="w-3 h-3 rotate-[-90deg]" />
                  </Link>
                </div>
              ) : (
                <p className="text-xs md:text-sm text-muted-foreground">Välj vilka cookies du vill tillåta</p>
              )}
            </div>
          </div>

          {/* Middle: Settings Panel (when open) */}
          {showSettingsPanel && (
            <div className="grid grid-cols-2 gap-3 lg:flex lg:flex-wrap lg:gap-2">
              <CookieOption 
                label="Nödvändiga"
                checked={true}
                disabled={true}
              />
              
              <CookieOption 
                label="Funktionella"
                checked={preferences.functional}
                onChange={(checked) => setPreferences({...preferences, functional: checked})}
              />
              
              <CookieOption 
                label="Analytiska"
                checked={preferences.analytics}
                onChange={(checked) => setPreferences({...preferences, analytics: checked})}
              />
              
              <CookieOption 
                label="Marknadsföring"
                checked={preferences.marketing}
                onChange={(checked) => setPreferences({...preferences, marketing: checked})}
              />
            </div>
          )}

          {/* Right: Actions */}
          <div className="flex flex-col sm:flex-row lg:flex-row gap-2 lg:flex-shrink-0 lg:ml-auto">
            {!showSettingsPanel ? (
              <>
                <Button 
                  onClick={acceptAll}
                  size="sm"
                  className="w-full h-9"
                >
                  Acceptera alla
                </Button>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setShowSettingsPanel(true)}
                    variant="outline"
                    size="sm"
                    className="flex-1 h-9"
                  >
                    Anpassa
                  </Button>
                  <Button 
                    onClick={rejectNonEssential}
                    variant="outline"
                    size="sm"
                    className="flex-1 h-9"
                  >
                    Endast nödvändiga
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Button 
                  onClick={acceptSelected}
                  size="sm"
                  className="w-full h-9"
                >
                  Spara inställningar
                </Button>
                <Button 
                  onClick={() => setShowSettingsPanel(false)}
                  variant="outline"
                  size="sm"
                  className="w-full h-9"
                >
                  Tillbaka
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

// Helper component for cookie options
const CookieOption = ({ 
  label, 
  checked, 
  disabled = false,
  onChange 
}: { 
  label: string; 
  checked: boolean; 
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}) => (
  <div 
    className={`flex flex-col items-center gap-2 p-2.5 rounded-lg border bg-muted/30 ${disabled ? 'opacity-60' : 'cursor-pointer hover:bg-muted/50'} transition-colors`}
    onClick={() => !disabled && onChange && onChange(!checked)}
  >
    <Checkbox 
      checked={checked}
      disabled={disabled}
      onCheckedChange={onChange ? (checked) => onChange(!!checked) : undefined}
      className="pointer-events-none"
    />
    <label className={`text-xs md:text-sm font-medium leading-tight text-center ${!disabled && 'cursor-pointer'}`}>
      {label}
    </label>
  </div>
);
